// 스킬 추가 효과(docs/SKILL-TREE.md 4·5장): 진홍 초승달 검기와 랭크 5·캡스톤 판정(Ev.SkillBurst).
// 검기와 지연 판정은 영웅 시간(히트스톱·일시정지 동안 멈춤)으로 진행한다. 검기는 발사 때 정한 길로 나가는 세계 투사체라서,
// 캔슬·회피·피격으로 영웅 동작이 바뀌어도 계속 날아간다. 판 초기화·장면 전환·사망 때 모두 지운다.
// 슬롯은 모두 미리 만든다(프레임 루프 안에서 할당하지 않는다).
import { MOB_CAPACITY, SKILLS } from "../shared/config";
import { Ev } from "../shared/events";
import { BURST, BurstKind, CRESCENT, type BurstKindId } from "../shared/growth";
import { Scene, combatScene } from "../shared/types";
import { headingTo, type HitMark } from "./combat";
import type { GameSim } from "./game";

const WAVE_SLOTS = 2;
const DELAY_SLOTS = 4;
/** 검기 벽 검사 간격(m). 얇은 벽 칸을 건너뛰지 않을 만큼 촘촘하다 */
const WALL_STEP = 0.25;

interface Wave {
  active: boolean;
  x: number;
  z: number;
  dx: number;
  dz: number;
  mult: number;
  /** 판정 효과(쌍월): 연계 배율을 곱하지 않는다 */
  plain: boolean;
  age: number;
  /** 발사 지점에서 지금까지 나간 거리(m). 벽에 막히면 거기서 멈춘다 */
  front: number;
  readonly mark: HitMark;
}
interface Delayed {
  active: boolean;
  kind: BurstKindId;
  /** 남은 영웅 틱. 예약한 다음 틱부터 줄어 0이 되면 발동한다 */
  left: number;
  x: number;
  z: number;
  /** 돌진 궤적의 끝(DashWake 전용) */
  x1: number;
  z1: number;
  dx: number;
  dz: number;
  mult: number;
}

export class SkillEffects {
  private readonly waves: Wave[] = [];
  private readonly delayed: Delayed[] = [];
  /** 이번 돌진의 시작점(돌진 끝에 궤적을 예약할 때 쓴다) */
  private dashX = 0;
  private dashZ = 0;

  constructor(private readonly g: GameSim) {
    for (let i = 0; i < WAVE_SLOTS; i++) {
      this.waves.push({ active: false, x: 0, z: 0, dx: 0, dz: 0, mult: 0, plain: false, age: 0, front: 0, mark: { mobs: new Uint8Array(MOB_CAPACITY), boss: false, rewards: 0, kills: 0, mass: false, linkStage: 0 } });
    }
    for (let i = 0; i < DELAY_SLOTS; i++) {
      this.delayed.push({ active: false, kind: BurstKind.DashWake, left: 0, x: 0, z: 0, x1: 0, z1: 0, dx: 0, dz: 0, mult: 0 });
    }
  }

  /** 판 초기화·장면 전환·사망: 진행 중 검기와 예약된 판정을 모두 지운다 */
  clear(): void {
    for (const w of this.waves) w.active = false;
    for (const d of this.delayed) d.active = false;
  }

  /** 돌진을 시작한 위치(같은 돌진의 궤적 시작점) */
  dashStart(x: number, z: number): void {
    this.dashX = x;
    this.dashZ = z;
  }

  /** 돌진 끝 뒤 BURST.dashWake.delay 틱에 핏빛 궤적(돌진 경로 같은 폭) */
  scheduleDashWake(x1: number, z1: number): void {
    const d = this.freeDelayed();
    if (!d) return;
    d.active = true; d.kind = BurstKind.DashWake; d.left = BURST.dashWake.delay;
    d.x = this.dashX; d.z = this.dashZ; d.x1 = x1; d.z1 = z1; d.dx = 0; d.dz = 0; d.mult = BURST.dashWake.mult;
  }

  /** 낙화 착지 뒤 여진(반경 BURST.aftershock.radius 원) */
  scheduleAftershock(x: number, z: number): void {
    const d = this.freeDelayed();
    if (!d) return;
    d.active = true; d.kind = BurstKind.Aftershock; d.left = BURST.aftershock.delay;
    d.x = x; d.z = z; d.x1 = x; d.z1 = z; d.dx = 0; d.dz = 0; d.mult = BURST.aftershock.mult;
  }

  /** 초승달 발사 뒤 쌍월: 같은 길(발사 지점·방향)로 위력 배율을 줄여 한 번 더 나간다 */
  scheduleTwinMoon(x: number, z: number, dx: number, dz: number): void {
    const d = this.freeDelayed();
    if (!d) return;
    d.active = true; d.kind = BurstKind.TwinMoon; d.left = BURST.twinMoon.delay;
    // 쌍월은 표의 위력 그대로(BURST.twinMoon.mult). 랭크 배율·연계 배율은 곱하지 않는다(판정 효과, SKILL-TREE 5장)
    d.x = x; d.z = z; d.x1 = x; d.z1 = z; d.dx = dx; d.dz = dz; d.mult = BURST.twinMoon.mult;
  }

  /** 진홍 초승달 검기 발사. 시작점과 방향은 발사 순간에 고정되고, 이후 영웅이 움직여도 바뀌지 않는다 */
  launchWave(x: number, z: number, dx: number, dz: number, mult: number, plain = false): void {
    let w: Wave | undefined;
    for (const slot of this.waves) if (!slot.active) { w = slot; break; }
    if (!w) return;
    w.active = true; w.x = x; w.z = z; w.dx = dx; w.dz = dz; w.mult = mult; w.plain = plain; w.age = 0; w.front = 0;
    w.mark.mobs.fill(0); w.mark.boss = false; w.mark.rewards = 0; w.mark.kills = 0; w.mark.mass = false; w.mark.mp = false;
    // 연계 단계는 발사 순간 값으로 고정한다(날아가는 동안 다른 스킬로 캔슬해 단계가 올라도 한 발의 피해는 같다)
    w.mark.linkStage = this.g.link.stage;
  }

  /** 폭풍의 눈(랭크 5, 폭풍 마지막 틱): 즉시 반경 안을 친다 */
  stormEye(x: number, z: number): void {
    this.burstCircle(BurstKind.StormEye, x, z, BURST.stormEye.radius, BURST.stormEye.mult);
  }

  /** 진홍 개화(캡스톤, 연계 3단 피니시): 즉시 반경 안을 친다 */
  bloom(x: number, z: number): void {
    this.burstCircle(BurstKind.Bloom, x, z, BURST.bloom.radius, BURST.bloom.mult);
  }

  /** 뇌명 검기(캡스톤, 기본 공격 마무리타 판정 순간): 전방 선분을 친다 */
  thunder(x: number, z: number, facing: number): void {
    const len = BURST.thunder.length, dx = Math.sin(facing), dz = Math.cos(facing);
    const e = this.g.emit(Ev.SkillBurst);
    e.x = x; e.z = z; e.a = BurstKind.Thunder; e.b = len; e.c = facing; e.d = BURST.thunder.width;
    this.g.combat.burstLine(x, z, x + dx * len, z + dz * len, BURST.thunder.width, BURST.thunder.mult);
  }

  /** 영웅 시간 한 틱(히트스톱·일시정지에서는 호출되지 않는다). 영웅 동작 처리 전에 부른다 */
  step(): void {
    if (!combatScene(this.g.flow.scene)) return;
    for (const d of this.delayed) {
      if (d.active && --d.left <= 0) { d.active = false; this.fire(d); }
    }
    for (const w of this.waves) if (w.active) this.advance(w);
  }

  // ── 내부 ────────────────────────────────────────

  private freeDelayed(): Delayed | undefined {
    for (const d of this.delayed) if (!d.active) return d;
    return undefined;
  }

  private burstCircle(kind: BurstKindId, x: number, z: number, radius: number, mult: number): void {
    const e = this.g.emit(Ev.SkillBurst);
    e.x = x; e.z = z; e.a = kind; e.b = radius; e.c = 0; e.d = 0;
    this.g.combat.burstCircle(x, z, radius, mult);
  }

  private fire(d: Delayed): void {
    switch (d.kind) {
      case BurstKind.DashWake: {
        const width = SKILLS[0].radius * 2, length = Math.hypot(d.x1 - d.x, d.z1 - d.z);
        const e = this.g.emit(Ev.SkillBurst);
        e.x = d.x; e.z = d.z; e.a = BurstKind.DashWake; e.b = length; e.c = headingTo(d.x, d.z, d.x1, d.z1, 0); e.d = width;
        this.g.combat.burstLine(d.x, d.z, d.x1, d.z1, width, d.mult);
        break;
      }
      case BurstKind.Aftershock:
        this.burstCircle(BurstKind.Aftershock, d.x, d.z, BURST.aftershock.radius, d.mult);
        break;
      case BurstKind.TwinMoon: {
        const e = this.g.emit(Ev.SkillBurst);
        e.x = d.x; e.z = d.z; e.a = BurstKind.TwinMoon; e.b = CRESCENT.length; e.c = Math.atan2(d.dx, d.dz); e.d = CRESCENT.width;
        this.launchWave(d.x, d.z, d.dx, d.dz, d.mult, true);
        break;
      }
      default:
        break;
    }
  }

  /**
   * 검기 한 틱 전진. 벽(걸을 수 없는 칸·닫힌 문)에 닿으면 거기서 멈춘다. 이번 구간의 적만 맞히며 같은 적은 한 번만 맞는다.
   */
  private advance(w: Wave): void {
    const g = this.g, prev = w.front, target = Math.min(CRESCENT.length, prev + CRESCENT.length / CRESCENT.travelTicks);
    let reach = target, blocked = false;
    for (let p = Math.min(prev + WALL_STEP, target); ; p = Math.min(p + WALL_STEP, target)) {
      if (!g.walkable(w.x + w.dx * p, w.z + w.dz * p, 0)) {
        reach = Math.max(prev, p - WALL_STEP);
        blocked = true;
        break;
      }
      if (p >= target) break;
    }
    w.age++;
    w.front = reach;
    if (reach > prev) {
      g.combat.waveLine(w.x + w.dx * prev, w.z + w.dz * prev, w.x + w.dx * reach, w.z + w.dz * reach, CRESCENT.width, w.mult, w.mark, w.plain);
    }
    if (blocked || reach >= CRESCENT.length || w.age >= CRESCENT.travelTicks) w.active = false;
  }
}

